import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Cpu, 
  Zap, 
  Trophy, 
  Target, 
  Copy,
  Check,
  Sword,
  Shield,
  Brain,
  Activity,
  ChevronRight,
  Sparkles,
  Wallet,
  BarChart3,
  History,
  Settings,
  ArrowUpRight,
  Loader2,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import NFTMintModal from './NFTMintModal';

interface BattleRecord {
  id: number;
  round: number;
  date: string;
  result: 'win' | 'loss';
  rank: number;
  kills: number;
  prize: number;
}

interface Agent {
  id: string;
  name: string;
  strategy: 'aggressive' | 'balanced' | 'defensive' | 'custom';
  strategyName: string;
  battles: number;
  wins: number;
  kills: number;
  top3: number;
  createdAt: string;
  config: {
    aggression: number;
    defense: number;
    mobility: number;
    utility: number;
  };
}

const mockBattleRecords: BattleRecord[] = [
  { id: 1, round: 169, date: '2026-03-25', result: 'loss', rank: 15, kills: 3, prize: 0 },
  { id: 2, round: 168, date: '2026-03-24', result: 'loss', rank: 8, kills: 5, prize: 0 },
  { id: 3, round: 167, date: '2026-03-23', result: 'win', rank: 1, kills: 12, prize: 10000 },
  { id: 4, round: 166, date: '2026-03-22', result: 'win', rank: 2, kills: 8, prize: 5000 },
  { id: 5, round: 165, date: '2026-03-21', result: 'loss', rank: 23, kills: 2, prize: 0 },
];

const strategyIcons = {
  aggressive: Sword,
  balanced: Activity,
  defensive: Shield,
  custom: Brain,
};

const strategyColors = {
  aggressive: 'text-red-500 bg-red-500/10 border-red-500/30',
  balanced: 'text-gold bg-gold/10 border-gold/30',
  defensive: 'text-blue-500 bg-blue-500/10 border-blue-500/30',
  custom: 'text-purple-500 bg-purple-500/10 border-purple-500/30',
};

const MIN_WITHDRAWAL = 1000;

export default function MyAgent() {
  const { user } = useAuth();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [credits, setCredits] = useState(1500);
  const [balance, setBalance] = useState(2500); // 可提现的 $MON 余额
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'settings'>('overview');
  const [showMintModal, setShowMintModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawStep, setWithdrawStep] = useState<'input' | 'confirming' | 'success'>('input');
  const [txHash, setTxHash] = useState('');

  useEffect(() => {
    const savedAgent = localStorage.getItem('rumbleclaw_agent');
    const savedCredits = localStorage.getItem('rumbleclaw_credits');
    if (savedAgent) {
      const agentData = JSON.parse(savedAgent);
      setAgent({
        ...agentData,
        battles: 12,
        wins: 5,
        kills: 47,
        top3: 8,
        strategyName: agentData.strategy === 'aggressive' ? 'Berserker' : 
                     agentData.strategy === 'defensive' ? 'Fortress' : 
                     agentData.strategy === 'balanced' ? 'Tactician' : 'Custom',
        config: {
          aggression: agentData.strategy === 'aggressive' ? 85 : agentData.strategy === 'defensive' ? 30 : 50,
          defense: agentData.strategy === 'defensive' ? 80 : agentData.strategy === 'aggressive' ? 25 : 50,
          mobility: 60,
          utility: 45,
        }
      });
    }
    if (savedCredits) {
      setCredits(parseInt(savedCredits));
    }
  }, []);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!user) {
    return (
      <section className="px-4 md:px-8 min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-surface border border-line flex items-center justify-center">
            <Cpu size={40} className="text-muted" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Connect Wallet</h1>
          <p className="text-muted text-sm">Connect your wallet to view your agent</p>
        </div>
      </section>
    );
  }

  if (!agent) {
    return (
      <>
        <section className="px-4 md:px-8 py-12 md:py-20">
          <div className="max-w-[480px] mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold/10 border border-gold/30 rounded-full mb-6">
                <Sparkles size={14} className="text-gold" />
                <span className="text-xs font-medium text-gold">Genesis Agent</span>
              </div>
              <h1 className="text-3xl font-bold mb-2">Mint Your Agent</h1>
              <p className="text-muted text-sm">Create your AI battle companion</p>
            </div>

            <div className="p-6 border border-gold/30 rounded-2xl bg-gold/5">
              <div className="w-20 h-20 mx-auto mb-5 rounded-2xl bg-surface border border-line flex items-center justify-center">
                <Cpu size={40} className="text-gold" />
              </div>
              <div className="space-y-2.5 mb-5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted">Mint Cost</span>
                  <span className="font-mono">100 $MON</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Gas Fee</span>
                  <span className="font-mono text-green-500">~0.001</span>
                </div>
                <div className="pt-2.5 border-t border-line flex justify-between font-medium">
                  <span>Total</span>
                  <span className="font-mono text-gold">~100 $MON</span>
                </div>
              </div>
              <button 
                onClick={() => setShowMintModal(true)}
                className="w-full py-3.5 bg-gold text-bg rounded-xl font-semibold hover:bg-white transition-colors"
              >
                Mint Agent NFT
              </button>
            </div>
          </div>
        </section>
        
        <NFTMintModal 
          isOpen={showMintModal} 
          onClose={() => setShowMintModal(false)}
          onSuccess={(agentData) => {
            setAgent(agentData);
            setShowMintModal(false);
          }}
        />
      </>
    );
  }

  const winRate = ((agent.wins / agent.battles) * 100).toFixed(0);
  const top3Rate = ((agent.top3 / agent.battles) * 100).toFixed(0);
  const StrategyIcon = strategyIcons[agent.strategy];

  return (
    <section className="px-4 md:px-8 py-8 md:py-12">
      <div className="max-w-[1200px] mx-auto">
        {/* Top Bar */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-xl font-bold">My Agent</h1>
          <a 
            href="/recharge" 
            className="px-4 py-2 bg-gold text-bg rounded-lg font-semibold text-sm hover:bg-white transition-colors flex items-center gap-2"
          >
            <Wallet size={16} />
            Recharge
          </a>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Agent Info */}
          <div className="space-y-6">
            {/* Agent Card */}
            <div className="p-6 border border-line rounded-2xl">
              <div className="flex items-start gap-4 mb-5">
                <div className={cn(
                  "w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 border-2",
                  strategyColors[agent.strategy]
                )}>
                  <Cpu size={28} />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-bold truncate mb-1">{agent.name}</h2>
                  <span className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border",
                    strategyColors[agent.strategy]
                  )}>
                    <StrategyIcon size={10} />
                    {agent.strategyName}
                  </span>
                </div>
              </div>
              
              <div className="pt-4 border-t border-line">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted">Agent ID</span>
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono">{agent.id.slice(0, 8)}...{agent.id.slice(-4)}</code>
                    <button 
                      onClick={() => handleCopy(agent.id)}
                      className="text-muted hover:text-gold transition-colors"
                    >
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* $MON Balance Card */}
            <div className="p-6 border border-gold/30 rounded-2xl bg-gold/5">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm text-muted">$MON Balance</span>
                <span className="text-[10px] px-1.5 py-0.5 bg-green-500/10 text-green-500 rounded">Withdrawable</span>
              </div>
              <div className="text-3xl font-mono font-bold text-gold mb-3">
                {balance.toLocaleString()}
              </div>
              <button
                onClick={() => setShowWithdrawModal(true)}
                disabled={balance < MIN_WITHDRAWAL}
                className={cn(
                  "w-full py-2.5 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2",
                  balance >= MIN_WITHDRAWAL
                    ? "bg-gold text-bg hover:bg-white"
                    : "bg-muted text-muted cursor-not-allowed"
                )}
              >
                {balance >= MIN_WITHDRAWAL ? (
                  <>
                    <ArrowUpRight size={16} />
                    Withdraw
                  </>
                ) : (
                  <>
                    Min {MIN_WITHDRAWAL.toLocaleString()} $MON
                  </>
                )}
              </button>
            </div>

            {/* Credits Card */}
            <div className="p-6 border border-line rounded-2xl">
              <div className="text-sm text-muted mb-1">Battle Credits</div>
              <div className="text-3xl font-mono font-bold text-ink mb-3">
                {credits.toLocaleString()}
              </div>
              <a 
                href="/recharge" 
                className="inline-flex items-center gap-1.5 text-sm text-gold hover:underline"
              >
                <Zap size={14} />
                Add Credits
              </a>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 border border-line rounded-xl text-center">
                <div className="text-2xl font-mono font-bold text-ink">{agent.battles}</div>
                <div className="text-xs text-muted mt-0.5">Battles</div>
              </div>
              <div className="p-4 border border-line rounded-xl text-center">
                <div className="text-2xl font-mono font-bold text-green-500">{agent.wins}</div>
                <div className="text-xs text-muted mt-0.5">Wins</div>
              </div>
              <div className="p-4 border border-line rounded-xl text-center">
                <div className="text-2xl font-mono font-bold text-ink">{winRate}%</div>
                <div className="text-xs text-muted mt-0.5">Win Rate</div>
              </div>
              <div className="p-4 border border-line rounded-xl text-center">
                <div className="text-2xl font-mono font-bold text-gold">{top3Rate}%</div>
                <div className="text-xs text-muted mt-0.5">Top 3</div>
              </div>
            </div>
          </div>

          {/* Right Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Strategy Section */}
            <div className="p-6 border border-line rounded-2xl">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-semibold flex items-center gap-2">
                  <BarChart3 size={18} className="text-gold" />
                  Battle Strategy
                </h3>
                <button className="text-xs text-gold hover:underline">Edit</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Aggression', val: agent.config.aggression, color: 'bg-red-500' },
                  { label: 'Defense', val: agent.config.defense, color: 'bg-blue-500' },
                  { label: 'Mobility', val: agent.config.mobility, color: 'bg-gold' },
                  { label: 'Utility', val: agent.config.utility, color: 'bg-purple-500' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-xl font-mono font-bold mb-2">{stat.val}%</div>
                    <div className="h-2 bg-surface rounded-full overflow-hidden mb-2">
                      <div className={cn("h-full rounded-full", stat.color)} style={{ width: `${stat.val}%` }} />
                    </div>
                    <div className="text-xs text-muted">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tabs */}
            <div className="border border-line rounded-2xl overflow-hidden">
              <div className="flex border-b border-line">
                {[
                  { id: 'overview', label: 'Overview', icon: BarChart3 },
                  { id: 'history', label: 'History', icon: History },
                  { id: 'settings', label: 'Settings', icon: Settings },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={cn(
                      "flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors",
                      activeTab === tab.id
                        ? "text-gold bg-gold/5"
                        : "text-muted hover:text-ink hover:bg-surface/50"
                    )}
                  >
                    <tab.icon size={16} />
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {activeTab === 'overview' && (
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-muted mb-4">Recent Performance</h4>
                    {mockBattleRecords.slice(0, 3).map((record) => (
                      <div 
                        key={record.id}
                        className="flex items-center justify-between p-4 bg-surface rounded-xl"
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-10 h-10 rounded-lg flex items-center justify-center",
                            record.result === 'win' 
                              ? "bg-green-500/10 text-green-500" 
                              : "bg-red-500/10 text-red-500"
                          )}>
                            {record.result === 'win' ? <Trophy size={16} /> : <Target size={16} />}
                          </div>
                          <div>
                            <div className="font-medium">Round {record.round}</div>
                            <div className="text-xs text-muted">{record.kills} kills · Rank #{record.rank}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={cn(
                            "font-mono font-bold",
                            record.result === 'win' ? "text-green-500" : "text-muted"
                          )}>
                            {record.result === 'win' ? `+${record.prize.toLocaleString()}` : '0'}
                          </div>
                          <div className="text-[10px] text-muted">{record.date}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'history' && (
                  <div className="space-y-2">
                    {mockBattleRecords.map((record) => (
                      <div 
                        key={record.id}
                        className="flex items-center justify-between p-4 hover:bg-surface rounded-xl transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-10 h-10 rounded-lg flex items-center justify-center",
                            record.result === 'win' 
                              ? "bg-green-500/10 text-green-500" 
                              : "bg-red-500/10 text-red-500"
                          )}>
                            {record.result === 'win' ? <Trophy size={16} /> : <Target size={16} />}
                          </div>
                          <div>
                            <div className="font-medium">Round {record.round}</div>
                            <div className="text-xs text-muted">{record.kills} kills · Rank #{record.rank}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={cn(
                            "font-mono font-bold",
                            record.result === 'win' ? "text-green-500" : "text-muted"
                          )}>
                            {record.result === 'win' ? `+${record.prize.toLocaleString()}` : '0'}
                          </div>
                          <div className="text-[10px] text-muted">{record.date}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'settings' && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-muted mb-2 block">Agent Name</label>
                      <div className="flex gap-2">
                        <input 
                          type="text" 
                          defaultValue={agent.name}
                          className="flex-1 bg-surface border border-line rounded-lg px-3 py-2.5 text-sm focus:border-gold focus:outline-none"
                        />
                        <button className="px-4 py-2.5 bg-gold text-bg rounded-lg text-sm font-medium hover:bg-white transition-colors">
                          Save
                        </button>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-line">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted">Created</span>
                        <span className="text-sm">{agent.createdAt}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Withdraw Modal */}
      <AnimatePresence>
        {showWithdrawModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-bg/95 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-[420px] bg-surface border border-line rounded-2xl overflow-hidden"
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-line flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center">
                    <ArrowUpRight size={20} className="text-gold" />
                  </div>
                  <div>
                    <h3 className="font-bold text-ink">Withdraw $MON</h3>
                    <p className="text-xs text-muted">Transfer to wallet</p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setShowWithdrawModal(false);
                    setWithdrawStep('input');
                    setWithdrawAmount('');
                  }}
                  className="text-muted hover:text-ink transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6">
                {withdrawStep === 'input' && (
                  <div className="space-y-5">
                    {/* Balance Info */}
                    <div className="p-4 bg-surface border border-line rounded-xl">
                      <div className="text-sm text-muted mb-1">Available Balance</div>
                      <div className="text-2xl font-mono font-bold text-gold">
                        {balance.toLocaleString()} $MON
                      </div>
                    </div>

                    {/* Amount Input */}
                    <div>
                      <label className="text-sm text-muted mb-2 block">Withdraw Amount</label>
                      <div className="relative">
                        <input
                          type="number"
                          value={withdrawAmount}
                          onChange={(e) => setWithdrawAmount(e.target.value)}
                          placeholder={`Min ${MIN_WITHDRAWAL.toLocaleString()}`}
                          className="w-full px-4 py-3 bg-surface border border-line rounded-xl text-ink placeholder:text-muted focus:outline-none focus:border-gold/50 transition-colors"
                        />
                        <button
                          onClick={() => setWithdrawAmount(balance.toString())}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gold hover:underline"
                        >
                          MAX
                        </button>
                      </div>
                      <div className="flex justify-between mt-2 text-xs">
                        <span className="text-muted">Min: {MIN_WITHDRAWAL.toLocaleString()} $MON</span>
                        <span className="text-muted">Max: {balance.toLocaleString()} $MON</span>
                      </div>
                    </div>

                    {/* Gas Fee */}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted">Gas Fee</span>
                      <span className="font-mono">~0.001 $MON</span>
                    </div>

                    {/* Receive Amount */}
                    <div className="flex justify-between text-sm pt-3 border-t border-line">
                      <span className="text-muted">You will receive</span>
                      <span className="font-mono font-bold text-gold">
                        {withdrawAmount ? Math.max(0, parseFloat(withdrawAmount) - 0.001).toFixed(3) : '0'} $MON
                      </span>
                    </div>

                    {/* Withdraw Button */}
                    <button
                      onClick={() => setWithdrawStep('confirming')}
                      disabled={!withdrawAmount || parseFloat(withdrawAmount) < MIN_WITHDRAWAL || parseFloat(withdrawAmount) > balance}
                      className={cn(
                        "w-full py-3.5 rounded-xl font-semibold transition-colors",
                        withdrawAmount && parseFloat(withdrawAmount) >= MIN_WITHDRAWAL && parseFloat(withdrawAmount) <= balance
                          ? "bg-gold text-bg hover:bg-white"
                          : "bg-muted text-muted cursor-not-allowed"
                      )}
                    >
                      {parseFloat(withdrawAmount) > balance 
                        ? 'Insufficient Balance' 
                        : parseFloat(withdrawAmount) < MIN_WITHDRAWAL && withdrawAmount
                          ? `Min ${MIN_WITHDRAWAL.toLocaleString()} $MON`
                          : 'Confirm Withdrawal'
                      }
                    </button>
                  </div>
                )}

                {withdrawStep === 'confirming' && (
                  <div className="py-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gold/10 flex items-center justify-center">
                      <Loader2 size={32} className="text-gold animate-spin" />
                    </div>
                    <h4 className="text-lg font-bold mb-2">Processing Withdrawal</h4>
                    <p className="text-sm text-muted mb-6">
                      Withdrawing {parseFloat(withdrawAmount).toLocaleString()} $MON to your wallet
                    </p>
                    <div className="h-2 bg-surface rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-gold rounded-full"
                        initial={{ width: '0%' }}
                        animate={{ width: '100%' }}
                        transition={{ duration: 2 }}
                        onAnimationComplete={() => {
                          const fakeHash = '0x' + Array(64).fill(0).map(() => Math.floor(Math.random() * 16).toString(16)).join('');
                          setTxHash(fakeHash);
                          setBalance(prev => prev - parseFloat(withdrawAmount));
                          setWithdrawStep('success');
                        }}
                      />
                    </div>
                  </div>
                )}

                {withdrawStep === 'success' && (
                  <div className="py-6 text-center">
                    <motion.div 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center"
                    >
                      <Check size={32} className="text-green-500" />
                    </motion.div>
                    <h4 className="text-lg font-bold mb-2">Withdrawal Successful!</h4>
                    <p className="text-sm text-muted mb-4">
                      {parseFloat(withdrawAmount).toLocaleString()} $MON has been sent to your wallet
                    </p>
                    {txHash && (
                      <div className="mb-6">
                        <div className="text-xs text-muted mb-1">Transaction Hash</div>
                        <code className="px-3 py-1.5 bg-surface rounded text-xs font-mono">
                          {txHash.slice(0, 20)}...{txHash.slice(-8)}
                        </code>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setShowWithdrawModal(false);
                        setWithdrawStep('input');
                        setWithdrawAmount('');
                      }}
                      className="px-6 py-2.5 bg-gold text-bg rounded-lg font-semibold hover:bg-white transition-colors"
                    >
                      Done
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <NFTMintModal 
        isOpen={showMintModal} 
        onClose={() => setShowMintModal(false)}
        onSuccess={(agentData) => {
          setAgent(agentData);
          setShowMintModal(false);
        }}
      />
    </section>
  );
}
